import { Check, Search } from "lucide-react";
import { matchSorter } from "match-sorter";
import { useMemo, useState } from "react";
import {
  monthOf,
  todayIso,
  type Category,
  type CategoryId,
  type Income,
  type IsoMonth,
  incomeChoices,
} from "@budgeter/shared";
import { useBudget, useSnapshot } from "../../data/StoreContext";
import { categoryGroups } from "../../data/selectors";
import { BalancePill, balanceTone, cn, Sheet } from "../../ui";

/** A transaction's budget target: a category, income, or nothing (uncategorised). */
export type CategoryChoice = { categoryId: CategoryId } | { income: Income } | null;

export function sameChoice(a: CategoryChoice, b: CategoryChoice): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  if ("categoryId" in a) {
    return "categoryId" in b && a.categoryId === b.categoryId;
  }
  return "income" in b && a.income === b.income;
}

export const incomeLabels: Record<Income, string> = {
  nextMonth: "Income for next month",
  thisMonth: "Income for this month",
};

export interface CategoryPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: CategoryChoice;
  onChange: (choice: CategoryChoice) => void;
  /** The month whose available amounts are shown. */
  month?: IsoMonth;
  /** Offer the two income choices (default true). */
  allowIncome?: boolean;
}

const rowClass =
  "flex min-h-[48px] w-full items-center gap-3 border-b border-line-soft px-1 text-left text-[15.5px] text-ink";
const headingClass = "px-1 pt-3 pb-1 text-[12px] font-bold tracking-[.5px] text-muted uppercase";

interface SearchRow {
  category: Category;
  master: string;
}

/** Searchable category sheet showing each category's available amount (SPEC §11.3). */
export function CategoryPicker({
  open,
  onOpenChange,
  value,
  onChange,
  month = monthOf(todayIso()),
  allowIncome = true,
}: CategoryPickerProps) {
  const s = useSnapshot();
  const budget = useBudget(month);
  const [query, setQuery] = useState("");
  const groups = useMemo(() => categoryGroups(s), [s]);
  const q = query.trim();
  const rows = useMemo<SearchRow[]>(
    () =>
      groups.flatMap((g) => g.categories.map((category) => ({ category, master: g.master.name }))),
    [groups],
  );
  const matches = useMemo(
    () =>
      q
        ? matchSorter(rows, q, {
            keys: ["category.name", { key: "master", threshold: matchSorter.rankings.CONTAINS }],
          })
        : null,
    [rows, q],
  );

  function choose(choice: CategoryChoice) {
    onChange(choice);
    setQuery("");
    onOpenChange(false);
  }

  const categoryRow = (c: Category) => {
    const cell = budget.cell(c.id, month);
    const selected = value !== null && "categoryId" in value && value.categoryId === c.id;
    return (
      <button
        key={c.id}
        type="button"
        className={rowClass}
        aria-current={selected ? "true" : undefined}
        onClick={() => choose({ categoryId: c.id })}
      >
        <span className={cn("min-w-0 flex-1 truncate", selected && "font-extrabold")}>
          {c.name}
        </span>
        {selected && <Check size={16} className="text-accent" aria-hidden />}
        <BalancePill
          cents={cell.available}
          tone={balanceTone(cell.available, cell.budgeted, c.overspend === "carry")}
        />
      </button>
    );
  };

  const incomeRows = allowIncome
    ? incomeChoices.filter((k) => !q || incomeLabels[k].toLowerCase().includes(q.toLowerCase()))
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Category">
      <label className="sticky top-0 z-10 mt-1 flex h-[42px] items-center gap-2 rounded-[10px] bg-bg px-3">
        <Search size={16} className="text-muted" aria-hidden />
        <span className="sr-only">Search categories</span>
        <input
          type="search"
          placeholder="Search categories"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-[16px] text-ink outline-none"
        />
      </label>
      {matches ? (
        <>
          {matches.map((r) => categoryRow(r.category))}
          {matches.length === 0 && incomeRows.length === 0 && (
            <p className="py-4 text-[14px] text-muted">No category matches "{q}".</p>
          )}
        </>
      ) : (
        groups
          .filter((g) => g.categories.length > 0)
          .map((g) => (
            <section key={g.master.id} aria-label={g.master.name}>
              <h3 className={headingClass}>{g.master.name}</h3>
              {g.categories.map(categoryRow)}
            </section>
          ))
      )}
      {incomeRows.length > 0 && (
        <>
          <h3 className={headingClass}>Income</h3>
          {incomeRows.map((k) => {
            const selected = value !== null && "income" in value && value.income === k;
            return (
              <button
                key={k}
                type="button"
                className={rowClass}
                aria-current={selected ? "true" : undefined}
                onClick={() => choose({ income: k })}
              >
                <span className={cn("flex-1", selected && "font-extrabold")}>
                  {incomeLabels[k]}
                </span>
                {selected && <Check size={16} className="text-accent" aria-hidden />}
              </button>
            );
          })}
        </>
      )}
    </Sheet>
  );
}
